import React, { useState } from 'react';
import '../../../blocks/CommsList/CommsList.scss';
import Icon from 'components/blocks/Icon/Icon';
import NoData from 'components/pages/noData/NoData';

const PAGE_SIZE = 5;

interface FormItem {
    CodigoExpediente?: string | number;
    EstadoFormularioDescripcion?: string;
    EstadoFormulario?: number;
    FechaAceptacionSolicitud?: string;
    [key: string]: any;
}

interface ManagementFormCardListProps {
    dataSource: FormItem[];
    onSelect: (args: { data: FormItem }) => void;
}

const STATUS_COLOR: Record<number, { bg: string; color: string; border: string }> = {
    1: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' }, // Pendiente — azul
    2: { bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' }, // En proceso — ámbar
    3: { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' }, // Aceptado — verde
    4: { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' }, // Rechazado — rojo
    5: { bg: '#F9FAFB', color: '#6B7280', border: '#E5E7EB' }, // Cancelado — gris
};

const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ManagementFormCardList: React.FC<ManagementFormCardListProps> = ({ dataSource, onSelect }) => {
    const [page, setPage] = useState(1);
    const [selectedIdx, setSelectedIdx] = useState<number | null>(null);


    if (!dataSource || dataSource.length === 0) {
        return <NoData message="No hay solicitudes previas registradas." />;
    }

    const totalPages = Math.ceil(dataSource.length / PAGE_SIZE);
    const paged = dataSource.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const pageOffset = (page - 1) * PAGE_SIZE;

    return (
        <div className="comms mb-4">
            {paged.map((item, idx) => {
                const absoluteIdx = pageOffset + idx;
                const isSelected = selectedIdx === absoluteIdx;
                const statusStyle = STATUS_COLOR[Number(item.EstadoFormulario)] ?? STATUS_COLOR[5];
                return (
                    <div
                        key={idx}
                        className="comm"
                        role="button"
                        tabIndex={0}
                        onClick={() => { setSelectedIdx(absoluteIdx); onSelect({ data: item }); }}
                        onKeyDown={(e) => e.key === 'Enter' && (setSelectedIdx(absoluteIdx), onSelect({ data: item }))}
                        aria-label={`Ver expediente ${item.CodigoExpediente ?? ''}, estado: ${item.EstadoFormularioDescripcion ?? ''}`}
                        aria-pressed={isSelected}
                        style={isSelected ? {
                            borderLeft: '3px solid var(--c-teal-700, #0d7a6e)',
                            background: '#c8ebe6',
                            transform: 'scale(1.015)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                            transition: 'all 0.15s ease',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        } : {
                            transition: 'all 0.15s ease',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}
                    >
                        {/* Izquierda: icono + expediente + estado */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <div className="comm__icon" style={{ background: statusStyle.bg, color: statusStyle.color, flexShrink: 0 }}>
                                <Icon name="file-text" size={16} />
                            </div>
                            <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--c-navy-700, #172E56)', whiteSpace: 'nowrap' }}>
                                Exp. {item.CodigoExpediente ?? '—'}
                            </span>
                            <span style={{
                                padding: '3px 10px',
                                borderRadius: 'var(--r-pill, 999px)',
                                fontSize: 'var(--fs-xs)',
                                fontWeight: 600,
                                background: statusStyle.bg,
                                color: statusStyle.color,
                                border: `1px solid ${statusStyle.border}`,
                                whiteSpace: 'nowrap',
                            }}>
                                {item.EstadoFormularioDescripcion ?? '—'}
                            </span>
                        </div>

                        {/* Derecha: fecha + chevron */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>
                                {formatDate(item.FechaAceptacionSolicitud)}
                            </span>
                            <Icon name="arrow-right" size={14} />
                        </div>
                    </div>
                );
            })}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, paddingTop: 16, paddingBottom: 4 }}>
                    <button
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, borderRadius: '50%',
                            border: '1px solid var(--border-soft, #ddd)',
                            background: page === 1 ? 'var(--bg-surface, #f5f5f5)' : '#fff',
                            color: page === 1 ? 'var(--fg-muted, #aaa)' : 'var(--c-navy-700, #1a3a5c)',
                            cursor: page === 1 ? 'not-allowed' : 'pointer',
                            fontSize: 12,
                        }}
                        aria-label="Página anterior"
                    >
                        <Icon name="arrow-left" size={16} />
                    </button>
                    <span style={{ fontSize: 'var(--fs-sm, 13px)', color: 'var(--fg-muted, #666)', minWidth: 60, textAlign: 'center' }}>
                        Página {page} de {totalPages}
                    </span>
                    <button
                        disabled={page === totalPages}
                        onClick={() => setPage(p => p + 1)}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, borderRadius: '50%',
                            border: '1px solid var(--border-soft, #ddd)',
                            background: page === totalPages ? 'var(--bg-surface, #f5f5f5)' : '#fff',
                            color: page === totalPages ? 'var(--fg-muted, #aaa)' : 'var(--c-navy-700, #1a3a5c)',
                            cursor: page === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: 12,
                        }}
                        aria-label="Página siguiente"
                    >
                        <Icon name="arrow-right" size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ManagementFormCardList;
